/**
 * =============================================================================
 * SPEQ55 RFP Pro - Login Page
 * =============================================================================
 *
 * This component provides the user login interface. It handles:
 *
 * - Username/email and password input
 * - Form validation with user feedback
 * - Login submission and error handling
 * - "Remember me" functionality
 * - Links to password reset
 * - Redirect after successful login
 *
 * DESIGN DECISIONS:
 * -----------------
 * 1. Centered card layout for focus on the form
 * 2. Clear error messages for failed attempts
 * 3. Loading state during submission to prevent double-submit
 * 4. Accessible form with proper labels and ARIA attributes
 *
 * SECURITY CONSIDERATIONS:
 * ------------------------
 * - No password hints or username enumeration
 * - Rate limiting is handled on the backend
 * - Password field uses type="password"
 * - Form data not stored in browser history
 *
 * ACCESSIBILITY:
 * --------------
 * - Form inputs have associated labels
 * - Error messages are announced to screen readers
 * - Focus management after submission
 * - Keyboard navigation support
 *
 * @module pages/auth/Login
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import speq55Logo from '../../assets/speq55-logo.png';

// =============================================================================
// LOGIN PAGE COMPONENT
// =============================================================================

/**
 * Login page component.
 *
 * Renders a login form with:
 * - Username/email input
 * - Password input
 * - Remember me checkbox
 * - Submit button
 * - Forgot password link
 *
 * On successful login, redirects to the originally requested page
 * (stored in location.state.from) or to the dashboard.
 *
 * @returns {JSX.Element} Login page
 */
function Login() {
  // ===========================================================================
  // HOOKS
  // ===========================================================================

  // Auth context for login function and state
  const { login, isAuthenticated, isLoading: authLoading, user } = useAuth();

  // Navigation for redirect after login
  const navigate = useNavigate();

  // Location to get redirect destination
  const location = useLocation();

  // ===========================================================================
  // STATE
  // ===========================================================================

  /**
   * Form input values.
   * Using a single state object for all form fields.
   */
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
    rememberMe: false,
  });

  /**
   * Form validation errors.
   * Keyed by field name.
   */
  const [errors, setErrors] = useState({});

  /**
   * General error message (e.g., from failed login attempt).
   */
  const [generalError, setGeneralError] = useState('');

  /**
   * Loading state during form submission.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ===========================================================================
  // REDIRECT IF ALREADY AUTHENTICATED
  // ===========================================================================

  /**
   * Validates if a user has permission to access a given path based on their role.
   *
   * @param {string} path - The path to validate
   * @param {string} userRole - The user's role
   * @returns {boolean} True if user can access the path
   */
  const canAccessPath = (path, userRole) => {
    // Admin routes require specific roles
    if (path.startsWith('/app/admin')) {
      const adminRoles = ['super_admin', 'admin_sales', 'org_admin'];
      return adminRoles.includes(userRole);
    }
    // All authenticated users can access /app routes
    return path.startsWith('/app');
  };

  /**
   * If user is already authenticated, redirect to dashboard.
   * This handles cases where user navigates to /login while logged in.
   */
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/app';

      // Validate that user has permission to access the redirect path
      // If not, redirect to /app instead to avoid Access Denied errors
      const destination = canAccessPath(from, user?.role) ? from : '/app';

      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state, user]);

  // ===========================================================================
  // FORM HANDLERS
  // ===========================================================================

  /**
   * Handles input field changes.
   * Updates formData state and clears field-specific errors.
   *
   * @param {Object} event - Input change event
   */
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    // For checkboxes, use checked; for other inputs, use value
    const fieldValue = type === 'checkbox' ? checked : value;

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Clear any existing error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear general error when user starts typing
    if (generalError) {
      setGeneralError('');
    }
  };

  /**
   * Validates the form before submission.
   *
   * @returns {boolean} True if form is valid
   */
  const validateForm = () => {
    const newErrors = {};

    // Username/email validation
    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Username or email is required';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission.
   * Validates form, attempts login, handles errors and redirect.
   *
   * @param {Object} event - Form submit event
   */
  const handleSubmit = async (event) => {
    // Prevent default form submission (page refresh)
    event.preventDefault();

    // Clear previous errors
    setGeneralError('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Set loading state
    setIsSubmitting(true);

    try {
      // Attempt login
      const result = await login(formData.usernameOrEmail, formData.password);

      if (result.success) {
        // Login successful - redirect
        // Validate redirect path against user's role to prevent Access Denied errors
        const from = location.state?.from?.pathname || '/app';
        const userRole = result.user?.role;

        // Check if user has permission to access the redirect path
        const destination = canAccessPath(from, userRole) ? from : '/app';

        navigate(destination, { replace: true });
      } else {
        // Login failed - show error message
        setGeneralError(result.error || 'Login failed. Please try again.');
      }
    } catch {
      // Unexpected error
      setGeneralError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  // Show loading if checking auth status
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/*
        Main container - centered card
        Using max-w-md for optimal form width
      */}
      <div className="max-w-md w-full space-y-8">
        {/* Header Section */}
        <div className="text-center">
          {/* Logo/Brand */}
          <div className="flex justify-center mb-4">
            <img src={speq55Logo} alt="SPEQ55" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-blue-600">RFP Pro</h1>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your credentials to access the platform
          </p>
        </div>

        {/* Login Form Card */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10">
          {/*
            General Error Alert
            Displayed when login fails with a message from the server
          */}
          {generalError && (
            <div
              className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded"
              role="alert"
              aria-live="polite"
            >
              <div className="flex">
                {/* Error icon */}
                <svg
                  className="h-5 w-5 text-red-500 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-red-700">{generalError}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            {/* Username/Email Field */}
            <div>
              <label htmlFor="usernameOrEmail" className="block text-sm font-medium text-gray-700">
                Username or Email
              </label>
              <div className="mt-1">
                <input
                  id="usernameOrEmail"
                  name="usernameOrEmail"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.usernameOrEmail}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`
                    appearance-none block w-full px-3 py-2 border rounded-md shadow-sm
                    placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500
                    sm:text-sm
                    ${errors.usernameOrEmail ? 'border-red-500 text-red-900' : 'border-gray-300'}
                    ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}
                  `}
                  placeholder="Enter your username or email"
                  aria-invalid={errors.usernameOrEmail ? 'true' : 'false'}
                  aria-describedby={errors.usernameOrEmail ? 'usernameOrEmail-error' : undefined}
                />
              </div>
              {/* Field-specific error */}
              {errors.usernameOrEmail && (
                <p id="usernameOrEmail-error" className="mt-2 text-sm text-red-600" role="alert">
                  {errors.usernameOrEmail}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={`
                    appearance-none block w-full px-3 py-2 border rounded-md shadow-sm
                    placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500
                    sm:text-sm
                    ${errors.password ? 'border-red-500 text-red-900' : 'border-gray-300'}
                    ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}
                  `}
                  placeholder="Enter your password"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
              </div>
              {/* Field-specific error */}
              {errors.password && (
                <p id="password-error" className="mt-2 text-sm text-red-600" role="alert">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password Row */}
            <div className="flex items-center justify-between">
              {/* Remember Me Checkbox */}
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              {/* Forgot Password Link */}
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`
                  w-full flex justify-center py-2.5 px-4 border border-transparent
                  rounded-md shadow-sm text-sm font-medium text-white
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  ${
                    isSubmitting
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    {/* Loading spinner */}
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500">
          Need help?{' '}
          <a
            href="mailto:support@speq55.com"
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default Login;
